import { Button } from '#GameFrontend/components/ui/button';
import { useLoading } from '#GameFrontend/contexts/LoadingContext';
import Disciplines from '#GameFrontend/unauthorized/components/register/Disciplines';
import Talents from '#GameFrontend/unauthorized/components/register/Talents';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
interface Starter {
	id: number;
	name: string;
	translationKey: string;
	imagePath: string;
	stats: {
		walk: number;
		trot: number;
		canter: number;
		agility: number;
		balance: number;
		speed: number;
		jumping: number;
		stamina: number;
	};
	disciplines: {
		showJumping: number;
		crossCountry: number;
		dressage: number;
		western: number;
		driving: number;
		racing: number;
	};
	starterGenes: {
		[key: string]: any;
	};
	mustHaveGenes: {
		[key: string]: any;
	};
}

interface Step1Props {
	bloodlines: Starter[];
	index: number;
	handleNext: () => void;
	handlePrevious: () => void;
	nextStep: () => void;
}

const Step1: React.FC<Step1Props> = ({ bloodlines, index, handleNext, handlePrevious, nextStep }) => {
	const { t } = useTranslation();
	const { setIsLoading } = useLoading();
	const [activeTab, setActiveTab] = useState('talents');
	const [isOverlayVisible, setIsOverlayVisible] = useState(false);
	const path = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORT !== '80' && process.env.REACT_APP_PORT !== '443' ? `:${process.env.REACT_APP_PORT}` : ''}/image/horses/starter/`;

	const handleTabClick = (tab: string) => {
		setActiveTab(tab);
	};

	const toggleOverlay = () => {
		setIsOverlayVisible(!isOverlayVisible);
	};

	useEffect(() => {
		const updateOverlay = () => {
			if (window.innerWidth >= 1280) {
				setIsOverlayVisible(true);
			} else if (window.innerWidth >= 1024) {
				setIsOverlayVisible(true);
			} else {
				setIsOverlayVisible(false);
			}
		};

		updateOverlay();
		window.addEventListener('resize', updateOverlay);
		return () => window.removeEventListener('resize', updateOverlay);
	}, []);

	const handleImageLoad = () => {
		setIsLoading(false);
	};

	const handleImageError = () => {
		setIsLoading(false);
	};

	return (
		<>
			{/* Second Container */}
			<div className={`pb-14 lg:py-6 lg:pl-6 xl:pl-0 flex flex-col flex-grow text-white text-center`}>
				<h2 className="text-4xl text-white mb-0 lg:mb-4 font-lobster text-center drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)] relative z-30">
					<div className="flex items-center justify-center">
						<Button
							className="bg-transparent border-0 shadow-none hover:bg-transparent"
							onClick={handlePrevious}
						>
							<div className="sprite-46 pl"></div>
						</Button>
						<svg
							width="65%"
							height="70"
							xmlns="http://www.w3.org/2000/svg"
							className="lg:hidden text-center inline-block"
						>
							<text x="50%" y="50" className="outlined-text svg-text" textAnchor="middle">
								{t(bloodlines[index].translationKey)}
							</text>
						</svg>
						<svg
							width="60%"
							height="40"
							xmlns="http://www.w3.org/2000/svg"
							className="hidden lg:inline-block text-center overflow-visible p-1"
						>
							<text x="50%" y="30" className="outlined-text svg-text" textAnchor="middle">
								{t(bloodlines[index].translationKey)}
							</text>
						</svg>
						<Button
							className="bg-transparent border-0 shadow-none hover:bg-transparent"
							onClick={handleNext}
						>
							<div className="sprite-46 pr"></div>
						</Button>
					</div>
				</h2>
				<div className="max-w-full xxl:max-w-[80%] lg:absolute xl:relative xxl:relative lg:ml-[-70px] xl:ml-0 xxl:ml-0 z-10">
					{/* Third Container */}
					{isOverlayVisible && (
						<div className="flex flex-col flex-grow overlay-content bg-blue-gradient top-[35%] md:top-[30%] backdrop-blur-sm  lg:hidden items-center pt-8 md:pt-5">
							{/* Tabs for Mobile */}
							<div className="tabs md:hidden">
								<div
									className={`tab ${activeTab === 'talents' ? 'active' : ''} font-normal mb-2 font-lobster  drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)]`}
									onClick={() => handleTabClick('talents')}
								>
									<svg width="120" height="35" xmlns="http://www.w3.org/2000/svg">
										<text x="60" y="30" className="outlined-text svg-text-sm" textAnchor="middle">
											{t('horse.talents')}
										</text>
									</svg>
								</div>
								<div
									className={`tab ${activeTab === 'disciplines' ? 'active' : ''} font-normal mb-2 font-lobster  drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)]`}
									onClick={() => handleTabClick('disciplines')}
								>
									<svg width="130" height="35" xmlns="http://www.w3.org/2000/svg">
										<text x="65" y="30" className="outlined-text svg-text-sm" textAnchor="middle">
											{t('horse.disciplines')}
										</text>
									</svg>
								</div>
							</div>
							<div className={`tab-content ${activeTab === 'talents' ? 'active' : ''}`}>
								<h2 className="font-normal mb-3 font-lobster  drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)] flex justify-center">
									<svg width="300" height="55" xmlns="http://www.w3.org/2000/svg">
										<text x="150" y="50" className="outlined-text svg-text-sm" textAnchor="middle">
											{t('horse.talents')}
										</text>
									</svg>
								</h2>
								<div className="flex justify-center">
									<Talents talents={bloodlines[index].stats} />
								</div>
							</div>

							<div className={`tab-content ${activeTab === 'disciplines' ? 'active' : ''}`}>
								<h2 className="font-normal font-lobster mt-8 drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)] flex justify-center">
									<svg width="300" height="55" xmlns="http://www.w3.org/2000/svg">
										<text x="150" y="35" className="outlined-text svg-text-sm" textAnchor="middle">
											{t('horse.disciplines')}
										</text>
									</svg>
								</h2>
								<Disciplines disciplines={bloodlines[index].disciplines} />
							</div>
						</div>
					)}
					<img
						src={bloodlines[index].imagePath === '' ? '' : `${path}${bloodlines[index].id}/preview.png`}
						alt="Horse"
						className="object-contain w-full h-full"
						onLoad={handleImageLoad}
						onError={handleImageError}
					/>
					<div className="flex flex-row flex-grow">
						<div className="relative flex justify-start flex-grow px-6  z-30">
							<Button
								variant="ghost"
								className="text-white hover:text-[#fb923c] hover:bg-white bg-[#fb923c] lg:hidden relative z-30"
								onClick={toggleOverlay}
							>
								{t('register.skills.button')}
							</Button>
						</div>
						<div className="flex flex-grow justify-end px-6 relative">
							<Button
								variant="ghost"
								className="text-white hover:text-[#fb923c] hover:bg-white bg-[#fb923c] lg:hidden relative z-30"
								onClick={nextStep}
							>
								{t('next.button')}
							</Button>
						</div>
					</div>
				</div>
			</div>

			{/* Third Container */}
			<div className={`pl-12 py-6 lg:pt-2 xl:p-6  flex-col flex-grow text-white hidden lg:flex`}>
				<div className="flex flex-col flex-grow ">
					<h2 className="text-3xl font-normal xl:mb-2 font-lobster text-[#fb923c] drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)]">
						<svg width="300" height="55" xmlns="http://www.w3.org/2000/svg">
							<text x="10" y="50" className="outlined-text svg-text-sm">
								{t('horse.talents')}
							</text>
						</svg>
					</h2>
					<div className="flex justify-center">
						<Talents talents={bloodlines[index].stats} />
					</div>
					<h2 className="text-3xl font-normal xl:mb-2 font-lobster text-[#fb923c] drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)]">
						<svg width="300" height="50" xmlns="http://www.w3.org/2000/svg">
							<text x="10" y="40" className="outlined-text svg-text-sm">
								{t('horse.disciplines')}
							</text>
						</svg>
					</h2>
					<Disciplines disciplines={bloodlines[index].disciplines} />
				</div>
				{/* Step Navigation */}
				<div className="flex justify-end mt-8">
					<Button
						variant="ghost"
						className="text-white hover:text-[#fb923c] hover:bg-white bg-[#fb923c] hidden lg:inline-block"
						onClick={nextStep}
					>
						{t('next.button') + ' >'}
					</Button>
				</div>
			</div>
		</>
	);
};
export default Step1;
